'use client';
import React, { useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import PublicTopbar from '@/components/PublicTopbar';
import {
  MapPin, Clock, Star, Users, Heart, ChevronLeft, ChevronRight,
  Shield, Check, X, Camera, Zap, Award, Info
} from 'lucide-react';

// ─── Package Data (mirrors PackagesGrid data) ─────────────────────────────────
const PACKAGES_DATA: Record<string, {
  id: string; name: string; operator: string; region: string; country: string;
  duration: number; category: string; badge: string | null; badgeColor: string;
  price: number; deposit: number; currency: string; rating: number; reviewCount: number;
  groupMin: number; groupMax: number; spotsLeft: number; image: string; imageAlt: string;
  tags: string[]; includes: string[]; excludes: string[];
  description: string; highlights: string[];
}> = {
  'pkg-001': {
    id: 'pkg-001', name: 'Maasai Mara Great Migration', operator: 'Savanna Adventures',
    region: 'Maasai Mara', country: 'Kenya', duration: 4, category: 'Big Five',
    badge: 'Best Seller', badgeColor: 'bg-safari-yellow text-gray-900',
    price: 128000, deposit: 38400, currency: 'KES', rating: 4.9, reviewCount: 312,
    groupMin: 2, groupMax: 8, spotsLeft: 3,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16d806e5f-1772249664669.png',
    imageAlt: 'Wildebeest migration across Maasai Mara river at golden hour',
    tags: ['Big Five', 'Migration', 'Photography'],
    includes: ['4 Game Drives', 'Full Board Accommodation', 'Park & Conservation Fees', 'Professional Guide', 'Drinking Water', 'Airport Transfers'],
    excludes: ['International Flights', 'Travel Insurance', 'Personal Items', 'Tips & Gratuities', 'Visa Fees'],
    description: 'Witness one of nature\'s greatest spectacles — the annual wildebeest migration across the Mara River. Over 1.5 million wildebeest, zebra, and gazelle thunder across the Maasai Mara in a breathtaking display of raw nature. This 4-day safari puts you at the heart of the action with expert guides who know exactly where to position you for the most dramatic river crossings.',
    highlights: ['Witness the Great Migration river crossings', 'Big Five sightings guaranteed', 'Sunset game drives with champagne', 'Bush breakfast in the savannah', 'Expert naturalist guide throughout'],
  },
  'pkg-002': {
    id: 'pkg-002', name: 'Amboseli Elephant Kingdom', operator: 'Wild Horizons Kenya',
    region: 'Amboseli', country: 'Kenya', duration: 3, category: 'Family',
    badge: 'Family Pick', badgeColor: 'bg-green-100 text-green-800',
    price: 89500, deposit: 26850, currency: 'KES', rating: 4.7, reviewCount: 187,
    groupMin: 2, groupMax: 12, spotsLeft: 7,
    image: 'https://images.unsplash.com/photo-1727252734589-147155b958be',
    imageAlt: 'Elephant herd walking in Amboseli with Mount Kilimanjaro in background',
    tags: ['Elephants', 'Family', 'Kilimanjaro Views'],
    includes: ['3 Game Drives', 'Half Board', 'Park Fees', 'Professional Guide', 'Sundowner Drinks'],
    excludes: ['International Flights', 'Travel Insurance', 'Personal Items', 'Lunch on Day 1'],
    description: 'Amboseli is Africa\'s elephant paradise — home to some of the largest tuskers on the continent, all set against the iconic backdrop of Mount Kilimanjaro. This family-friendly safari is perfectly paced for all ages, with morning and afternoon game drives that maximize wildlife sightings while leaving time to relax and soak in the scenery.',
    highlights: ['Africa\'s largest free-roaming elephant herds', 'Iconic Kilimanjaro backdrop photography', 'Family-friendly pace and activities', 'Maasai cultural village visit', 'Sundowner drinks at scenic viewpoint'],
  },
  'pkg-003': {
    id: 'pkg-003', name: 'Serengeti Luxury Tented Camp', operator: 'Tanzania Exclusive Safaris',
    region: 'Serengeti', country: 'Tanzania', duration: 5, category: 'Luxury',
    badge: 'Premium', badgeColor: 'bg-amber-100 text-amber-800',
    price: 285000, deposit: 85500, currency: 'KES', rating: 5.0, reviewCount: 94,
    groupMin: 2, groupMax: 6, spotsLeft: 2,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1fad7d61b-1772249789689.png',
    imageAlt: 'Luxury safari tent at sunset with giraffes in the background on Serengeti plains',
    tags: ['Luxury', 'Big Five', 'Sundowners'],
    includes: ['6 Game Drives', 'All Inclusive', 'Bush Dinner', 'Spa Access', 'Private Guide', 'Hot Air Balloon Option'],
    excludes: ['International Flights', 'Visa Fees', 'Hot Air Balloon (optional add-on)', 'Personal Shopping'],
    description: 'The Serengeti Luxury Tented Camp experience redefines what a safari can be. Sleep under canvas in a fully-appointed luxury tent with en-suite bathroom, private deck, and butler service — all while the sounds of the African bush surround you. With 6 game drives across 5 days, you\'ll cover the Serengeti\'s most productive wildlife zones.',
    highlights: ['Luxury tented accommodation with butler service', 'Private game drives in exclusive zones', 'Bush dinner under the stars', 'Spa and wellness treatments', 'Optional hot air balloon over the Serengeti'],
  },
  'pkg-004': {
    id: 'pkg-004', name: 'Tsavo East & West Explorer', operator: 'Red Elephant Safaris',
    region: 'Tsavo', country: 'Kenya', duration: 3, category: 'Adventure',
    badge: null, badgeColor: '', price: 67000, deposit: 20100, currency: 'KES',
    rating: 4.5, reviewCount: 142, groupMin: 2, groupMax: 10, spotsLeft: 9,
    image: 'https://images.unsplash.com/photo-1605302596046-1649bec79747',
    imageAlt: 'Red elephants walking in Tsavo National Park dry savannah landscape',
    tags: ['Red Elephants', 'Adventure', 'Value'],
    includes: ['4 Game Drives', 'Breakfast', 'Park Fees', 'Professional Guide'],
    excludes: ['Lunch & Dinner', 'International Flights', 'Travel Insurance', 'Personal Items'],
    description: 'Tsavo is Kenya\'s largest national park — a vast, wild landscape where the famous "red elephants" roam. Covered in red volcanic dust, these elephants are unlike any you\'ll see elsewhere. Tsavo East and West offer contrasting landscapes: the open plains of the East versus the lush, hilly terrain of the West.',
    highlights: ['Famous red elephants of Tsavo', 'Mzima Springs crystal-clear pools', 'Shetani lava flows geological wonder', 'Lugard Falls on the Galana River', 'Kenya\'s largest national park ecosystem'],
  },
  'pkg-005': {
    id: 'pkg-005', name: 'Ngorongoro Crater Rim Safari', operator: 'Crater View Expeditions',
    region: 'Ngorongoro', country: 'Tanzania', duration: 2, category: 'Day Trip',
    badge: 'New', badgeColor: 'bg-blue-100 text-blue-800',
    price: 54000, deposit: 16200, currency: 'KES', rating: 4.8, reviewCount: 67,
    groupMin: 1, groupMax: 6, spotsLeft: 5,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b039d2d6-1772252846157.png',
    imageAlt: 'Aerial view of Ngorongoro Crater with wildlife grazing on crater floor',
    tags: ['Crater', 'Big Five', 'Short Break'],
    includes: ['Crater Descent', 'Picnic Lunch', 'Guide', 'Conservation Fees'],
    excludes: ['International Flights', 'Accommodation', 'Personal Items'],
    description: 'The Ngorongoro Crater is the world\'s largest intact volcanic caldera and a UNESCO World Heritage Site. Descend into this natural amphitheatre where an estimated 25,000 animals live in a self-contained ecosystem. With the Big Five all present, it\'s one of Africa\'s most reliable wildlife viewing destinations.',
    highlights: ['World\'s largest intact volcanic caldera', 'Highest density of predators in Africa', 'Black rhino sightings possible', 'Flamingo-lined soda lake', 'UNESCO World Heritage Site'],
  },
  'pkg-006': {
    id: 'pkg-006', name: 'Lake Nakuru Flamingo & Rhino', operator: 'Rift Valley Safaris',
    region: 'Lake Nakuru', country: 'Kenya', duration: 2, category: 'Birding',
    badge: 'Eco Pick', badgeColor: 'bg-green-100 text-green-800',
    price: 42000, deposit: 12600, currency: 'KES', rating: 4.6, reviewCount: 203,
    groupMin: 2, groupMax: 8, spotsLeft: 12,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bf5d4063-1772249665223.png',
    imageAlt: 'Thousands of pink flamingos on Lake Nakuru with rhino in foreground',
    tags: ['Flamingos', 'Rhino', 'Birding'],
    includes: ['2 Game Drives', 'Breakfast', 'Park Fees', 'Guide'],
    excludes: ['Lunch & Dinner', 'International Flights', 'Personal Items'],
    description: 'Lake Nakuru is famous for its spectacular flamingo flocks that turn the lake pink, alongside both black and white rhino. This compact 2-day safari is perfect for a weekend escape or as part of a longer Kenya itinerary.',
    highlights: ['Pink flamingo flocks on the lake shore', 'Both black and white rhino sanctuary', 'Waterbuck and buffalo herds', 'Baboon Cliff panoramic viewpoint', 'Compact and accessible from Nairobi'],
  },
  'pkg-007': {
    id: 'pkg-007', name: 'Samburu Unique Wildlife Safari', operator: 'Northern Frontier Safaris',
    region: 'Samburu', country: 'Kenya', duration: 4, category: 'Unique Wildlife',
    badge: null, badgeColor: '', price: 115000, deposit: 34500, currency: 'KES',
    rating: 4.7, reviewCount: 88, groupMin: 2, groupMax: 8, spotsLeft: 6,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e738ff30-1775894306281.png',
    imageAlt: 'Reticulated giraffe and Grevy zebra in Samburu National Reserve dry landscape',
    tags: ['Rare Species', 'Off-the-beaten', 'Authentic'],
    includes: ['5 Game Drives', 'Full Board', 'Night Drive', 'Cultural Visit', 'Guide'],
    excludes: ['International Flights', 'Travel Insurance', 'Personal Items'],
    description: 'Samburu is home to the "Samburu Special Five" — species found nowhere else in Kenya: reticulated giraffe, Grevy\'s zebra, Beisa oryx, Somali ostrich, and gerenuk. This off-the-beaten-path safari offers an authentic, uncrowded experience in Kenya\'s arid north.',
    highlights: ['Samburu Special Five unique species', 'Night game drives for nocturnal wildlife', 'Samburu cultural village experience', 'Ewaso Nyiro River wildlife corridor', 'Uncrowded, authentic safari experience'],
  },
  'pkg-008': {
    id: 'pkg-008', name: 'Bwindi Gorilla Trekking Experience', operator: 'Uganda Wild Encounters',
    region: 'Bwindi', country: 'Uganda', duration: 3, category: 'Primate',
    badge: 'Limited Permits', badgeColor: 'bg-red-100 text-red-800',
    price: 340000, deposit: 102000, currency: 'KES', rating: 4.9, reviewCount: 156,
    groupMin: 1, groupMax: 4, spotsLeft: 2,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_151cea54f-1775894309532.png',
    imageAlt: 'Mountain gorilla family in Bwindi Impenetrable Forest Uganda green canopy',
    tags: ['Gorillas', 'Primates', 'Bucket List'],
    includes: ['Gorilla Permit', 'Guide', 'Forest Lodge', 'Full Board', 'Park Fees'],
    excludes: ['International Flights', 'Visa Fees', 'Travel Insurance', 'Personal Items'],
    description: 'Coming face-to-face with a mountain gorilla in the wild is a life-changing experience. Bwindi Impenetrable Forest is home to almost half the world\'s remaining mountain gorillas. Permits are strictly limited to protect these magnificent animals, making this one of the most exclusive wildlife encounters on earth.',
    highlights: ['One hour with a habituated gorilla family', 'Half the world\'s mountain gorillas live here', 'UNESCO World Heritage Forest', 'Expert gorilla tracker guides', 'Strictly limited permits — book early'],
  },
  'pkg-009': {
    id: 'pkg-009', name: 'Ol Pejeta Rhino Conservation', operator: 'Conservation Safari Co.',
    region: 'Ol Pejeta', country: 'Kenya', duration: 2, category: 'Conservation',
    badge: 'Eco Pick', badgeColor: 'bg-green-100 text-green-800',
    price: 78000, deposit: 23400, currency: 'KES', rating: 4.8, reviewCount: 119,
    groupMin: 2, groupMax: 10, spotsLeft: 8,
    image: 'https://images.unsplash.com/photo-1532574754390-44dc5c6780bb',
    imageAlt: 'White rhino grazing at Ol Pejeta conservancy with acacia trees at dusk',
    tags: ['Rhino', 'Conservation', 'Chimpanzees'],
    includes: ['Game Drives', 'Rhino Tracking', 'Half Board', 'Conservation Talk', 'Guide'],
    excludes: ['International Flights', 'Lunch on Day 1', 'Personal Items'],
    description: 'Ol Pejeta is East Africa\'s largest black rhino sanctuary and home to the last two northern white rhinos on earth. This conservation-focused safari gives you an intimate understanding of rhino protection efforts while enjoying exceptional wildlife viewing across the conservancy.',
    highlights: ['Last two northern white rhinos on earth', 'Largest black rhino sanctuary in East Africa', 'Chimpanzee sanctuary visit', 'Behind-the-scenes conservation briefing', 'Proceeds support rhino protection'],
  },
  'pkg-010': {
    id: 'pkg-010', name: 'Tarangire Baobab Wilderness', operator: 'Tanzania Exclusive Safaris',
    region: 'Tarangire', country: 'Tanzania', duration: 3, category: 'Photography',
    badge: null, badgeColor: '', price: 96000, deposit: 28800, currency: 'KES',
    rating: 4.6, reviewCount: 74, groupMin: 2, groupMax: 6, spotsLeft: 10,
    image: 'https://images.unsplash.com/photo-1564198729838-cb82ee0c733c',
    imageAlt: 'Ancient baobab trees at sunset in Tarangire National Park Tanzania with elephants',
    tags: ['Photography', 'Baobabs', 'Elephants'],
    includes: ['Photo Workshops', 'Full Board', 'Game Drives', 'Photography Guide'],
    excludes: ['International Flights', 'Camera Equipment', 'Personal Items'],
    description: 'Tarangire is Tanzania\'s best-kept secret — a landscape of ancient baobab trees, seasonal swamps, and enormous elephant herds. During the dry season, wildlife concentrates around the Tarangire River in extraordinary numbers. This photography-focused safari includes dedicated photo workshops with a professional wildlife photographer.',
    highlights: ['Ancient baobab trees — some over 1,000 years old', 'Largest elephant herds in Tanzania', 'Professional photography workshops', 'Seasonal swamp wildlife concentration', 'Less crowded than Serengeti'],
  },
  'pkg-011': {
    id: 'pkg-011', name: 'Murchison Falls Nile Safari', operator: 'Uganda Wild Encounters',
    region: 'Murchison Falls', country: 'Uganda', duration: 4, category: 'Adventure',
    badge: null, badgeColor: '', price: 132000, deposit: 39600, currency: 'KES',
    rating: 4.7, reviewCount: 91, groupMin: 2, groupMax: 10, spotsLeft: 7,
    image: 'https://images.unsplash.com/photo-1664690126073-15b4ec53b718',
    imageAlt: 'Hippos in the Nile River below Murchison Falls with lush green banks',
    tags: ['Nile Cruise', 'Big Five', 'Waterfalls'],
    includes: ['Nile Boat Safari', 'Full Board', 'Game Drives', 'Falls Hike', 'Guide'],
    excludes: ['International Flights', 'Visa Fees', 'Travel Insurance'],
    description: 'Murchison Falls is where the mighty Nile squeezes through a 7-metre gap in the rocks, creating one of the world\'s most powerful waterfalls. Uganda\'s largest national park surrounds it, offering exceptional game viewing including lions, elephants, giraffes, and the famous Nile boat safari with hippos and crocodiles.',
    highlights: ['World\'s most powerful waterfall', 'Nile boat safari with hippos & crocodiles', 'Rothschild giraffe — critically endangered', 'Hike to the top of the falls', 'Shoebill stork sightings possible'],
  },
  'pkg-012': {
    id: 'pkg-012', name: 'Selous Game Reserve Fly-In', operator: 'Southern Tanzania Expeditions',
    region: 'Selous', country: 'Tanzania', duration: 5, category: 'Luxury',
    badge: 'Fly-In', badgeColor: 'bg-purple-100 text-purple-800',
    price: 420000, deposit: 126000, currency: 'KES', rating: 4.9, reviewCount: 48,
    groupMin: 2, groupMax: 6, spotsLeft: 4,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_177e639c3-1772155358299.png',
    imageAlt: 'Small aircraft landing on bush airstrip in Selous Game Reserve Tanzania',
    tags: ['Fly-In', 'Luxury', 'Boat Safari'],
    includes: ['Charter Flight', 'All Inclusive', 'Boat Safari', 'Walking Safari', 'Private Guide'],
    excludes: ['International Flights', 'Visa Fees', 'Personal Shopping'],
    description: 'Selous is Africa\'s largest game reserve — a vast, wild wilderness that sees a fraction of the visitors of the northern parks. Accessible only by charter flight, this fly-in safari delivers an exclusive experience with boat safaris on the Rufiji River, walking safaris, and game drives in pristine, uncrowded bush.',
    highlights: ['Africa\'s largest game reserve', 'Exclusive fly-in access only', 'Rufiji River boat safari', 'Walking safaris in true wilderness', 'Wild dog sightings — Africa\'s rarest predator'],
  },
};

const REVIEWS = [
  { name: 'Priya Nair', country: 'India', rating: 5, text: 'An absolutely life-changing experience. The guide knew exactly where to find the animals and the accommodation was stunning.' },
  { name: 'James Okonkwo', country: 'Nigeria', rating: 5, text: 'The deposit system made it so easy to plan ahead. Everything was perfectly organized from start to finish.' },
  { name: 'Sarah Mitchell', country: 'UK', rating: 4, text: 'Incredible wildlife sightings every single day. The team was professional and passionate about conservation.' },
];

export default function PackageDetailPage() {
  const params = useParams();
  const router = useRouter();
  const id = params?.id as string;
  const pkg = PACKAGES_DATA[id];
  const [saved, setSaved] = useState(false);

  if (!pkg) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <PublicTopbar />
        <div className="flex-1 flex items-center justify-center flex-col gap-4 px-6">
          <div className="w-16 h-16 bg-safari-sand rounded-2xl flex items-center justify-center">
            <Info size={28} className="text-safari-earth" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Package Not Found</h1>
          <p className="text-gray-500 text-sm text-center max-w-sm">This package may have been removed or the link is incorrect.</p>
          <Link href="/safari-packages-listing" className="flex items-center gap-2 px-5 py-2.5 bg-safari-green text-white font-semibold rounded-xl text-sm hover:bg-green-800 transition-all">
            <ChevronLeft size={16} /> Browse All Packages
          </Link>
        </div>
      </div>
    );
  }

  const depositAmount = Math.round(pkg.price * 0.30);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicTopbar />

      {/* Hero Image */}
      <div className="relative h-72 md:h-96 lg:h-[480px] overflow-hidden">
        <AppImage
          src={pkg.image}
          alt={pkg.imageAlt}
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Back Button */}
        <div className="absolute top-4 left-4 md:top-6 md:left-6">
          <button
            onClick={() => router.back()}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white font-medium rounded-xl text-sm hover:bg-white/30 transition-all border border-white/20"
          >
            <ChevronLeft size={16} /> Back
          </button>
        </div>

        {/* Save Button */}
        <div className="absolute top-4 right-4 md:top-6 md:right-6">
          <button
            onClick={() => setSaved(!saved)}
            className={`p-2.5 rounded-xl backdrop-blur-sm border transition-all ${saved ? 'bg-red-500 border-red-500 text-white' : 'bg-white/20 border-white/20 text-white hover:bg-white/30'}`}
          >
            <Heart size={18} fill={saved ? 'currentColor' : 'none'} />
          </button>
        </div>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-screen-xl mx-auto">
            {pkg.badge && (
              <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-3 ${pkg.badgeColor}`}>
                {pkg.badge}
              </span>
            )}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">{pkg.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><MapPin size={14} />{pkg.region}, {pkg.country}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />{pkg.duration} days</span>
              <span className="flex items-center gap-1.5"><Star size={14} className="text-safari-yellow fill-safari-yellow" />{pkg.rating} ({pkg.reviewCount} reviews)</span>
              <span className="flex items-center gap-1.5"><Users size={14} />{pkg.groupMin}–{pkg.groupMax} people</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: Details */}
          <div className="lg:col-span-2 space-y-6">

            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {pkg.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-safari-sand text-safari-earth text-xs font-semibold rounded-full border border-amber-200">
                  {tag}
                </span>
              ))}
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-3">About This Safari</h2>
              <p className="text-gray-600 text-sm leading-relaxed">{pkg.description}</p>
            </div>

            {/* Highlights */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Zap size={18} className="text-safari-yellow" /> Highlights
              </h2>
              <ul className="space-y-3">
                {pkg.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-safari-yellow/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={11} className="text-safari-earth" />
                    </div>
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            {/* Includes / Excludes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Check size={16} className="text-safari-green" /> What's Included
                </h3>
                <ul className="space-y-2.5">
                  {pkg.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Check size={10} className="text-safari-green" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <X size={16} className="text-red-400" /> Not Included
                </h3>
                <ul className="space-y-2.5">
                  {pkg.excludes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-gray-500">
                      <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <X size={10} className="text-red-400" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Operator Info */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award size={18} className="text-safari-yellow" /> Your Operator
              </h2>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-safari-green rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {pkg.operator.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{pkg.operator}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Verified Onthego Safari Partner</p>
                  <div className="flex items-center gap-1 mt-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={11} className={s <= Math.floor(pkg.rating) ? 'text-safari-yellow fill-safari-yellow' : 'text-gray-200 fill-gray-200'} />
                    ))}
                    <span className="text-xs text-gray-500 ml-1">{pkg.rating} · {pkg.reviewCount} reviews</span>
                  </div>
                </div>
                <div className="ml-auto">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                    <Shield size={12} className="text-safari-green" />
                    <span className="text-xs font-semibold text-safari-green">Verified</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Camera size={18} className="text-safari-yellow" /> Traveler Reviews
              </h2>
              <div className="space-y-4">
                {REVIEWS.map((review, i) => (
                  <div key={i} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-safari-sand rounded-full flex items-center justify-center text-safari-earth font-bold text-sm flex-shrink-0">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{review.name}</p>
                        <p className="text-xs text-gray-400">{review.country}</p>
                      </div>
                      <div className="ml-auto flex gap-0.5">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} size={11} className={s <= review.rating ? 'text-safari-yellow fill-safari-yellow' : 'text-gray-200 fill-gray-200'} />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Price Header */}
                <div className="bg-safari-green p-5">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-1">Starting from</p>
                  <p className="text-3xl font-bold text-white tabular-nums">
                    KES {pkg.price.toLocaleString()}
                  </p>
                  <p className="text-sm text-white/70 mt-0.5">per person</p>
                </div>

                <div className="p-5 space-y-4">
                  {/* Deposit Info */}
                  <div className="flex items-center justify-between p-3 bg-safari-sand rounded-xl border border-amber-200">
                    <div>
                      <p className="text-xs font-semibold text-safari-earth">Secure with deposit</p>
                      <p className="text-lg font-bold text-gray-900 tabular-nums">KES {depositAmount.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">30% now</p>
                      <p className="text-xs text-gray-400">Balance before trip</p>
                    </div>
                  </div>

                  {/* Availability */}
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${pkg.spotsLeft <= 3 ? 'bg-red-500' : 'bg-safari-green'}`}></div>
                    <p className={`text-sm font-semibold ${pkg.spotsLeft <= 3 ? 'text-red-600' : 'text-safari-green'}`}>
                      {pkg.spotsLeft <= 3 ? `Only ${pkg.spotsLeft} spots left!` : `${pkg.spotsLeft} spots available`}
                    </p>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Duration', value: `${pkg.duration} days` },
                      { label: 'Group Size', value: `${pkg.groupMin}–${pkg.groupMax}` },
                      { label: 'Rating', value: `${pkg.rating} ★` },
                      { label: 'Reviews', value: `${pkg.reviewCount}` },
                    ].map(stat => (
                      <div key={stat.label} className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400">{stat.label}</p>
                        <p className="text-sm font-bold text-gray-900 mt-0.5">{stat.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Book CTA */}
                  <Link
                    href="/safari-booking"
                    className="w-full flex items-center justify-center gap-2 py-3.5 bg-safari-yellow text-gray-900 font-bold rounded-xl hover:bg-yellow-400 transition-all active:scale-[0.98] text-sm"
                  >
                    Book This Safari
                    <ChevronRight size={16} />
                  </Link>

                  {/* Trust Badges */}
                  <div className="space-y-2 pt-1">
                    {[
                      'Free cancellation up to 30 days before',
                      'Secure payment — SSL encrypted',
                      'Instant booking confirmation',
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                        <Shield size={12} className="text-safari-green flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Need Help */}
              <div className="bg-safari-sand rounded-2xl border border-amber-200 p-4">
                <p className="text-sm font-semibold text-safari-earth mb-1">Need help choosing?</p>
                <p className="text-xs text-gray-500 mb-3">Our safari experts are available 7 days a week.</p>
                <a
                  href="mailto:support@onthegosafari.com"
                  className="text-xs font-semibold text-safari-green hover:text-green-700 transition-colors"
                >
                  support@onthegosafari.com →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
